import {
  ContactSlugEnum,
  CountryEnum,
  getContactFieldTitle,
  setDateSince,
  setDateTill,
  setDateWithTimezone,
  toISOStringWithoutTimezone,
} from '@systemeio/ui'
import ContactFilterWrapper from 'modules/contacts/components/contact-filter-wrapper'
import {
  ContactFilterAffiliateSearch,
  ContactFilterContactStateSearch,
  ContactFilterCountrySearch,
  ContactFilterDateSearch,
  ContactFilterDefaultSearch,
  ContactFilterEmailActivitySearch,
  ContactFilterTagSearch,
} from 'modules/contacts/components/contact-filters'
import { contactFilterAdditionalTitles } from 'modules/contacts/constants/contact-filter-titles'
import {
  ContactFilterConditionEnum,
  ContactFilterDateConditionEnum,
  ContactSearchFieldEnum,
} from 'modules/contacts/enums/contact-enum'
import {
  ContactFieldApiInterface,
  ContactFilterContactStateInterface,
  ContactFilterDateInterface,
  ContactFilterInterface,
  ContactFilterTagInterface,
  ContactFilterTextInterface,
  ContactFilterType,
} from 'modules/contacts/types/contact-filter-interface'
import { getDefaultFilterValue } from 'modules/contacts/utils/get-default-filter-value'
import React, { useCallback, useRef } from 'react'
import { FilterableItemType } from 'shared/components/listing-layout'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'

const CONTACT_SEARCH_FIELDS_SORT_ORDER: (ContactSlugEnum | ContactSearchFieldEnum)[] = [
  ContactSlugEnum.Email,
  ContactSlugEnum.FirstName,
  ContactSlugEnum.Surname,
  ContactSlugEnum.StreetAddress,
  ContactSlugEnum.StreetNumber,
  ContactSlugEnum.Neighborhood,
  ContactSlugEnum.Postcode,
  ContactSlugEnum.City,
  ContactSlugEnum.State,
  ContactSlugEnum.Country,
  ContactSlugEnum.PhoneNumber,
  ContactSearchFieldEnum.tag,
  ContactSearchFieldEnum.created_at,
  ContactSearchFieldEnum.last_activity_date,
  ContactSearchFieldEnum.state,
  ContactSlugEnum.CompanyName,
  ContactSlugEnum.TaxNumber,
  ContactSearchFieldEnum.affiliate,
  ContactSearchFieldEnum.removed_at,
  ContactSearchFieldEnum.removed_reason_type,
]

interface RenderContactFiltersDataInterface {
  filtersReady: boolean
  filterFields?: ContactFieldApiInterface
  setFilter: React.Dispatch<React.SetStateAction<ContactFilterType>>
  filter: ContactFilterType
  needToCloseFilters: boolean
  setNeedToCloseFilters: React.Dispatch<React.SetStateAction<boolean>>
}

export const useRenderContactFilters = ({
  filtersReady,
  filterFields,
  setFilter,
  needToCloseFilters,
  filter,
  setNeedToCloseFilters,
}: RenderContactFiltersDataInterface): FilterableItemType<ContactFilterType>[] => {
  const clearClosingState = useCallback(() => setNeedToCloseFilters(false), [setNeedToCloseFilters])
  const lastFieldSlug = useRef<ContactSlugEnum | ContactSearchFieldEnum>()

  const { t } = useLocoTranslation()

  const { user } = useUser()

  const clearFilterField = useCallback(
    (slug: string) => setFilter(prev => ({ ...prev, [slug]: getDefaultFilterValue(slug) })),
    [setFilter],
  )

  const addFilter = (slug: string) => {
    setFilter(prev => ({
      ...prev,
      [slug]: [...prev[slug], ...getDefaultFilterValue(slug)],
    }))
  }

  const deleteFilter = (slug: string, index: number) => {
    setFilter(prev => {
      prev[slug].splice(index, 1)
      return { ...prev, [slug]: [...prev[slug]] }
    })
  }

  const baseFilters: FilterableItemType<ContactFilterType>[] =
    filterFields && filtersReady
      ? filterFields.items.map(filterEl => {
          switch (filterEl.slug) {
            case ContactSlugEnum.Country: {
              return {
                field: filterEl.slug,
                onRender: (value, onChange) => (
                  <ContactFilterWrapper
                    clearClosingState={clearClosingState}
                    needToCloseFilters={needToCloseFilters}
                    clearFilterField={clearFilterField}
                    isFetching={false}
                    filter={filter}
                    title={getContactFieldTitle(filterEl)}
                    key={`${filterEl.slug}-filter`}
                    addFilter={addFilter}
                    slug={filterEl.slug}
                    lastElement={lastFieldSlug.current === filterEl.slug}
                  >
                    <>
                      {(value as Array<CountryEnum | undefined>).map((el, idx) => (
                        <ContactFilterCountrySearch
                          index={idx}
                          deleteFilter={deleteFilter}
                          length={value.length}
                          key={`${filterEl.slug}-filter-${idx}`}
                          searchValue={el}
                          onChangeSearchValue={data => {
                            const newValue: Array<CountryEnum | undefined> = [
                              ...(value as Array<CountryEnum | undefined>),
                            ]
                            newValue[idx] = data
                            onChange(newValue)
                          }}
                          slug={ContactSlugEnum.Country}
                        />
                      ))}
                    </>
                  </ContactFilterWrapper>
                ),
              }
            }
            default: {
              return {
                field: filterEl.slug,
                onRender: (value, onChange) => (
                  <ContactFilterWrapper
                    clearClosingState={clearClosingState}
                    needToCloseFilters={needToCloseFilters}
                    clearFilterField={clearFilterField}
                    isFetching={false}
                    filter={filter}
                    title={getContactFieldTitle(filterEl)}
                    key={`${filterEl.slug}-${filterEl.fieldName}-filter`}
                    addFilter={addFilter}
                    slug={filterEl.slug}
                    lastElement={lastFieldSlug.current === filterEl.slug}
                  >
                    <>
                      {(value as Array<ContactFilterTextInterface>).map((el, idx) => (
                        <ContactFilterDefaultSearch
                          index={idx}
                          deleteFilter={deleteFilter}
                          length={value.length}
                          key={`${filterEl.slug}-filter-${idx}`}
                          searchValue={el.searchValue}
                          onChangeSearchValue={data => {
                            const newValue: ContactFilterTextInterface[] = [
                              ...(value as Array<ContactFilterTextInterface>),
                            ]
                            newValue[idx] = {
                              searchValue: data,
                              compareOperation: el.compareOperation,
                            }
                            onChange(newValue)
                          }}
                          slug={filterEl.slug}
                          condition={el.compareOperation}
                          onChangeCondition={data => {
                            const newValue = [...value]
                            newValue[idx] = {
                              searchValue:
                                data === ContactFilterConditionEnum.not_set ? '' : el.searchValue,
                              compareOperation: data,
                            }
                            onChange(newValue)
                          }}
                        />
                      ))}
                    </>
                  </ContactFilterWrapper>
                ),
              }
            }
          }
        })
      : []

  const additionalFilters: FilterableItemType<ContactFilterType>[] = [
    {
      field: ContactSearchFieldEnum.tag,
      onRender: (value, onChange) => (
        <ContactFilterWrapper
          clearClosingState={clearClosingState}
          needToCloseFilters={needToCloseFilters}
          clearFilterField={clearFilterField}
          isFetching={!filtersReady}
          filter={filter}
          title={t(contactFilterAdditionalTitles.search_field_tag)}
          key={`tag-filter`}
          addFilter={addFilter}
          slug={ContactSearchFieldEnum.tag}
          lastElement={lastFieldSlug.current === ContactSearchFieldEnum.tag}
        >
          <>
            {(value as Array<ContactFilterTagInterface>).map((el, idx) => (
              <ContactFilterTagSearch
                index={idx}
                deleteFilter={deleteFilter}
                length={value.length}
                key={`tag-filter-${idx}`}
                searchValue={el.searchValue ? Number(el.searchValue) : undefined}
                onChangeSearchValue={data => {
                  const newValue: ContactFilterTagInterface[] = (
                    value as Array<ContactFilterTagInterface>
                  ).map((e, i) => {
                    if (i === idx) {
                      return {
                        compareOperation: e.compareOperation,
                        searchValue: data ? String(data) : undefined,
                      }
                    } else {
                      return e
                    }
                  })
                  onChange(newValue)
                }}
                slug={ContactSearchFieldEnum.tag}
                condition={el.compareOperation}
                onChangeCondition={data => {
                  const newValue: ContactFilterTagInterface[] = (
                    value as Array<ContactFilterTagInterface>
                  ).map((e, i) => {
                    if (i === idx) {
                      return { compareOperation: data, searchValue: undefined }
                    } else {
                      return e
                    }
                  })
                  onChange(newValue)
                }}
              />
            ))}
          </>
        </ContactFilterWrapper>
      ),
    },
    {
      field: ContactSearchFieldEnum.created_at,
      onRender: (value, onChange) => (
        <ContactFilterWrapper
          clearClosingState={clearClosingState}
          needToCloseFilters={needToCloseFilters}
          clearFilterField={clearFilterField}
          isFetching={!filtersReady}
          filter={filter}
          title={t(contactFilterAdditionalTitles.search_field_created_at)}
          key={`created-at-filter`}
          addFilter={addFilter}
          slug={ContactSearchFieldEnum.created_at}
          lastElement={lastFieldSlug.current === ContactSearchFieldEnum.created_at}
        >
          <>
            {(value as Array<ContactFilterDateInterface>).map((el, idx) => (
              <ContactFilterDateSearch
                index={idx}
                deleteFilter={deleteFilter}
                length={value.length}
                key={`created-at-filter-${idx}`}
                searchValue={el.searchValue}
                onChangeSearchValue={data => {
                  const newValue: ContactFilterDateInterface[] = (
                    value as Array<ContactFilterDateInterface>
                  ).map((e, i) => {
                    if (i === idx) {
                      return { compareOperation: e.compareOperation, searchValue: data }
                    } else {
                      return e
                    }
                  })
                  onChange(newValue)
                }}
                slug={ContactSearchFieldEnum.created_at}
                condition={el.compareOperation}
                onChangeCondition={data => {
                  let date = setDateWithTimezone(el.searchValue || undefined, user?.timezone)
                  if (data === ContactFilterDateConditionEnum.less_than) {
                    date = setDateTill(date)
                  } else {
                    date = setDateSince(date)
                  }

                  const newValue: ContactFilterDateInterface[] = (
                    value as Array<ContactFilterDateInterface>
                  ).map((e, i) => {
                    if (i === idx) {
                      return {
                        compareOperation: data,
                        searchValue: toISOStringWithoutTimezone(date),
                      }
                    } else {
                      return e
                    }
                  })
                  onChange(newValue)
                }}
              />
            ))}
          </>
        </ContactFilterWrapper>
      ),
    },
    {
      field: ContactSearchFieldEnum.last_activity_date,
      onRender: (value, onChange) => (
        <ContactFilterWrapper
          clearClosingState={clearClosingState}
          needToCloseFilters={needToCloseFilters}
          clearFilterField={clearFilterField}
          isFetching={!filtersReady}
          filter={filter}
          title={t(contactFilterAdditionalTitles.search_field_last_activity_date)}
          key={`last-activity-date-filter`}
          addFilter={addFilter}
          slug={ContactSearchFieldEnum.last_activity_date}
          lastElement={lastFieldSlug.current === ContactSearchFieldEnum.last_activity_date}
        >
          <>
            {(value as Array<{ searchValue: string | null }>).map((el, idx) => (
              <ContactFilterEmailActivitySearch
                index={idx}
                deleteFilter={deleteFilter}
                length={value.length}
                key={`last-activity-date-filter-${idx}`}
                searchValue={el.searchValue}
                onChangeSearchValue={data => {
                  const newValue: ContactFilterInterface['last_activity_date'] = (
                    value as ContactFilterInterface['last_activity_date']
                  ).map((e, i) => {
                    if (i === idx) {
                      return { searchValue: data }
                    } else {
                      return e
                    }
                  })
                  onChange(newValue)
                }}
                slug={ContactSearchFieldEnum.last_activity_date}
              />
            ))}
          </>
        </ContactFilterWrapper>
      ),
    },
    {
      field: ContactSearchFieldEnum.state,
      onRender: (value, onChange) => (
        <ContactFilterWrapper
          clearClosingState={clearClosingState}
          needToCloseFilters={needToCloseFilters}
          clearFilterField={clearFilterField}
          isFetching={!filtersReady}
          filter={filter}
          title={t(contactFilterAdditionalTitles.search_field_state)}
          key={`contact-state-filter`}
          addFilter={addFilter}
          slug={ContactSearchFieldEnum.state}
          lastElement={lastFieldSlug.current === ContactSearchFieldEnum.state}
        >
          <>
            {(value as Array<ContactFilterContactStateInterface>).map((el, idx) => (
              <ContactFilterContactStateSearch
                index={idx}
                deleteFilter={deleteFilter}
                length={value.length}
                key={`state-filter-${idx}`}
                condition={el.searchValue}
                onChangeCondition={data => {
                  const newValue: ContactFilterContactStateInterface[] = (
                    value as Array<ContactFilterContactStateInterface>
                  ).map((e, i) => {
                    if (i === idx) {
                      return { searchValue: data }
                    } else {
                      return e
                    }
                  })
                  onChange(newValue)
                }}
                slug={ContactSearchFieldEnum.state}
              />
            ))}
          </>
        </ContactFilterWrapper>
      ),
    },
    {
      field: ContactSearchFieldEnum.affiliate,
      onRender: (value, onChange) => (
        <ContactFilterWrapper
          clearClosingState={clearClosingState}
          needToCloseFilters={needToCloseFilters}
          clearFilterField={clearFilterField}
          isFetching={!filtersReady}
          filter={filter}
          title={t(contactFilterAdditionalTitles.search_field_affiliate)}
          key={`affiliate-filter`}
          addFilter={addFilter}
          slug={ContactSearchFieldEnum.affiliate}
          lastElement={lastFieldSlug.current === ContactSearchFieldEnum.affiliate}
        >
          <>
            {(value as Array<{ searchValue: string }>).map((el, idx) => (
              <ContactFilterAffiliateSearch
                index={idx}
                deleteFilter={deleteFilter}
                length={value.length}
                key={`affiliate-filter-${idx}`}
                searchValue={el.searchValue}
                onChangeSearchValue={data => {
                  const newValue: ContactFilterInterface['affiliate'] = (
                    value as Array<{ searchValue: string }>
                  ).map((e, i) => {
                    if (i === idx) {
                      return { searchValue: data }
                    } else {
                      return e
                    }
                  })
                  onChange(newValue)
                }}
                slug={ContactSearchFieldEnum.affiliate}
              />
            ))}
          </>
        </ContactFilterWrapper>
      ),
    },
  ]

  const sortedFields = [...baseFilters, ...additionalFilters].sort((a, b) => {
    const indexA = CONTACT_SEARCH_FIELDS_SORT_ORDER.indexOf(
      a.field as ContactSlugEnum | ContactSearchFieldEnum,
    )
    const indexB = CONTACT_SEARCH_FIELDS_SORT_ORDER.indexOf(
      b.field as ContactSlugEnum | ContactSearchFieldEnum,
    )

    if (indexA === -1) return 1
    if (indexB === -1) return -1

    return indexA - indexB
  })

  lastFieldSlug.current = sortedFields[sortedFields.length - 1]?.field as
    | ContactSlugEnum
    | ContactSearchFieldEnum

  return sortedFields
}
